"use client";

import { ArrowRight, Newspaper, WrenchAdjustable } from "react-bootstrap-icons";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Spinner from "react-bootstrap/Spinner";

import { MAX_SHORT_FIELD } from "@/lib/contact";
import { NewsletterKind, validateNewsletterRequest } from "@/lib/newsletter";

import { InfoCard } from "./commercial/InfoCard";
import {
  Honeypot,
  InquiryErrors,
  useInquiryForm,
} from "./forms/InquiryForm";
import { PrivacyNotice } from "./forms/PrivacyNotice";

interface NewsletterInfoState {
  email: string;
  newsletters: NewsletterKind[];
  phone: string;
}

interface NewsletterInfoProps {
  description: string;
  icon: React.ReactNode;
  newsletter: NewsletterKind;
  title: string;
}

function NewsletterInfo({
  description,
  icon,
  newsletter,
  title,
}: NewsletterInfoProps) {
  const form = useInquiryForm<NewsletterInfoState>({
    initial: { email: "", newsletters: [newsletter], phone: "" },
    submitUrl: "/newsletter/subscribe/",
    fallback: "info@opensanctions.org",
    validate: validateNewsletterRequest,
  });

  return (
    <div className="mt-3 d-print-none">
      <InfoCard style="border" icon={icon} title={title}>
        {form.done ? (
          <p>
            <strong>Check your inbox.</strong> We&rsquo;ve sent you a confirmation link.
          </p>
        ) : (
          <>
            <p>{description}</p>
            <Form onSubmit={form.onSubmit} noValidate className="mt-3">
              <InquiryErrors errors={form.errors} />
              <Form.Group>
                <Form.Label
                  htmlFor={`newsletter-email-${newsletter}`}
                  className="visually-hidden"
                >
                  Email address
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    id={`newsletter-email-${newsletter}`}
                    type="email"
                    value={form.state.email}
                    onChange={(event) => form.set("email", event.target.value)}
                    maxLength={MAX_SHORT_FIELD}
                    placeholder="Email address"
                    required
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={form.submitting}
                    aria-label={form.submitting ? "Sending confirmation" : "Send confirmation"}
                  >
                    {form.submitting ? (
                      <Spinner
                        as="span"
                        size="sm"
                        animation="border"
                        role="status"
                        aria-hidden="true"
                      />
                    ) : (
                      <ArrowRight aria-hidden="true" />
                    )}
                  </Button>
                </InputGroup>
              </Form.Group>
              <PrivacyNotice subject="email" />
              <Honeypot
                value={form.state.phone}
                onChange={(value) => form.set("phone", value)}
              />
            </Form>
          </>
        )}
      </InfoCard>
    </div>
  );
}

export function ProductNewsletterInfo() {
  return (
    <NewsletterInfo
      newsletter="product_updates"
      title="Newsletter"
      description="Monthly news about product and data coverage improvements."
      icon={<Newspaper />}
    />
  );
}

export function TechnicalBulletinInfo() {
  return (
    <NewsletterInfo
      newsletter="technical_bulletin"
      title="Technical bulletin"
      description="Monthly announcements about data, APIs, and breaking changes."
      icon={<WrenchAdjustable />}
    />
  );
}
